/**
 * CANON GATE — risk hit-panel layout (#1–#N).
 *
 * New risk cards / panels are allowed ONLY through this component.
 * Read `riskCardLayoutCanon.js` before adding Risk #N UI.
 *
 * - Fill slots only. Do not invent a parallel shell or copy class strings.
 * - Content must not change outer width/height (no className/style escapes).
 * - Feature slot always reserved via RiskHitFeatureBlock (even when empty).
 *
 * Slot order (owned here — do not reorder in hit panels):
 *   riskNumber → badges → title → score → bar → feature → metrics → details → actions → note
 */
import RiskHitFeatureBlock from './RiskHitFeatureBlock';
import InfoHint from './InfoHint';
import { RISK_CARD_LAYOUT_CANON } from './riskCardLayoutCanon';
import { RISK_LABELS, riskBarStyle, riskScoreStyle } from './riskScoreStyles';

// Keep canon module referenced so AI/tools see the contract at the layout gate.
void RISK_CARD_LAYOUT_CANON;

/** Frozen layout tokens — do not duplicate these class strings in hit panels. */
export const RISK_HIT_LAYOUT = Object.freeze({
  /** Fills parent case card panel; never grows past it. */
  rootEmbedded: 'flex h-full min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden',
  rootStandalone:
    'flex h-full min-h-[40rem] w-full min-w-0 flex-col overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950/60 p-3.5 shadow-lg shadow-slate-200/70 dark:shadow-slate-950/20',
  header: 'mb-2.5 flex shrink-0 items-start justify-between gap-2',
  headerLeft: 'min-w-0',
  riskLabel: 'text-xs uppercase tracking-[0.2em] text-slate-500',
  badges: 'mt-1 flex max-h-12 flex-wrap items-center gap-1 overflow-hidden',
  title: 'mt-1 truncate text-lg font-semibold text-slate-900 dark:text-white',
  scoreWrap: 'shrink-0 text-right',
  scoreBadge: 'inline-flex rounded-full border px-2.5 py-1 text-xs font-medium',
  scoreLevel: 'mt-1 text-xs text-slate-500',
  barTrack: 'mb-2.5 h-1.5 shrink-0 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-900',
  barFill: 'h-full rounded-full',
  /** Height cage — timeline insets its own plot so end dots stay visible. */
  feature: 'mb-2.5 h-[9.25rem] shrink-0 overflow-hidden',
  body: 'flex min-h-0 flex-1 flex-col gap-2.5 overflow-hidden',
  metrics: 'min-h-0 shrink-0 overflow-hidden',
  details: 'shrink-0 overflow-hidden rounded-xl border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900/40',
  detailsSummary:
    'cursor-pointer px-3 py-1.5 text-sm text-slate-500 transition hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-300',
  detailsBody: 'max-h-28 space-y-2.5 overflow-y-auto border-t border-slate-200 px-3 py-2 dark:border-slate-800',
  actions: 'mt-auto flex shrink-0 flex-col gap-2 sm:flex-row sm:flex-wrap',
  actionBtn:
    'w-full rounded-xl border px-3 py-2 text-sm font-medium sm:min-w-[10rem] sm:flex-1 transition',
  actionHistory:
    'border-violet-400/30 bg-violet-400/10 text-violet-700 hover:border-violet-400/50 dark:text-violet-200',
  actionSecondary:
    'inline-flex items-center justify-center border-slate-300 bg-slate-50 text-slate-700 hover:border-slate-400 hover:bg-slate-100 dark:border-slate-600 dark:bg-slate-900/40 dark:text-slate-200 dark:hover:border-slate-500 dark:hover:bg-slate-900',
  actionLk:
    'border-sky-400/30 bg-sky-400/10 text-sky-700 hover:border-sky-400/50 dark:text-sky-200 disabled:cursor-not-allowed disabled:opacity-60',
  metricGrid:
    'grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)] items-baseline gap-x-3 gap-y-2 text-sm',
});

export function RiskHitMetricGrid({ rows }) {
  return (
    <dl className={RISK_HIT_LAYOUT.metricGrid}>
      {rows.map((row) => (
        <div key={row.key} className="contents">
          <dt className="min-w-0 text-slate-500">
            {row.hint ? (
              <span className="inline-flex min-w-0 max-w-full items-center gap-1 text-slate-500">
                <span className="truncate">{row.label}</span>
                <InfoHint text={row.hint} />
              </span>
            ) : (
              <span className="block truncate" title={row.label}>
                {row.label}
              </span>
            )}
          </dt>
          <dd
            className={`min-w-0 truncate text-right font-medium text-slate-900 dark:text-slate-100 ${
              row.numeric ? 'tabular-nums' : ''
            }`}
            title={typeof row.value === 'string' || typeof row.value === 'number' ? String(row.value) : undefined}
          >
            {row.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * @param {object} props
 * @param {1|2|3|4|number|string} props.riskNumber
 * @param {import('react').ReactNode} props.badges
 * @param {string} props.title
 * @param {import('react').ReactNode} props.scoreDisplay
 * @param {string} props.riskLevel
 * @param {number|null|undefined} props.scoreBarValue 0–100 for the bar width
 * @param {import('react').ReactNode} [props.feature] timeline / signal / forecast (size locked)
 * @param {import('react').ReactNode} props.metrics
 * @param {import('react').ReactNode} [props.details]
 * @param {import('react').ReactNode} [props.actions]
 * @param {import('react').ReactNode} [props.footerNote]
 * @param {boolean} [props.embedded] inside RiskCaseCard (default true)
 */
export default function RiskHitPanelLayout({
  riskNumber,
  badges,
  title,
  scoreDisplay,
  riskLevel,
  scoreBarValue = 0,
  feature = null,
  metrics,
  details = null,
  actions = null,
  footerNote = null,
  embedded = true,
}) {
  const Root = embedded ? 'div' : 'article';
  const bar = Number.isFinite(Number(scoreBarValue)) ? Math.min(Math.max(Number(scoreBarValue), 0), 100) : 0;

  return (
    <Root
      className={embedded ? RISK_HIT_LAYOUT.rootEmbedded : RISK_HIT_LAYOUT.rootStandalone}
      data-risk-hit-layout="canon"
      data-risk-number={riskNumber}
    >
      <div className={RISK_HIT_LAYOUT.header}>
        <div className={RISK_HIT_LAYOUT.headerLeft}>
          <p className={RISK_HIT_LAYOUT.riskLabel}>Риск #{riskNumber}</p>
          {badges ? <div className={RISK_HIT_LAYOUT.badges}>{badges}</div> : null}
          <h3 className={RISK_HIT_LAYOUT.title} title={typeof title === 'string' ? title : undefined}>
            {title}
          </h3>
        </div>
        <div className={RISK_HIT_LAYOUT.scoreWrap}>
          <span className={`${RISK_HIT_LAYOUT.scoreBadge} ${riskScoreStyle(bar, riskLevel)}`}>
            {scoreDisplay}
          </span>
          <p className={RISK_HIT_LAYOUT.scoreLevel}>{RISK_LABELS[riskLevel] || riskLevel}</p>
        </div>
      </div>

      <div className={RISK_HIT_LAYOUT.barTrack}>
        <div
          className={`${RISK_HIT_LAYOUT.barFill} ${riskBarStyle(bar, riskLevel)}`}
          style={{ width: `${bar}%` }}
        />
      </div>

      {/* Feature slot is always reserved — same height whether filled or empty. */}
      <div className={RISK_HIT_LAYOUT.feature}>
        {feature ?? <RiskHitFeatureBlock />}
      </div>

      <div className={RISK_HIT_LAYOUT.body}>
        <div className={RISK_HIT_LAYOUT.metrics}>{metrics}</div>

        {details ? (
          <details className={RISK_HIT_LAYOUT.details}>
            <summary className={RISK_HIT_LAYOUT.detailsSummary}>Подробности</summary>
            <div className={RISK_HIT_LAYOUT.detailsBody}>{details}</div>
          </details>
        ) : null}

        {actions ? <div className={RISK_HIT_LAYOUT.actions}>{actions}</div> : null}
        {footerNote ? <div className="shrink-0 overflow-hidden">{footerNote}</div> : null}
      </div>
    </Root>
  );
}
